import React, { useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { uploadFile } from '../uploadFile';
import CKE4Editor from './CKE4Editor';
import { theme, buttonClass } from '../ui/theme';
// 统一使用公共脚本 window.openFileManagerModal

const NoticeEditor = ({ onSuccess, onCancel }) => {
  const { t } = useTranslation();
  const [title, setTitle] = useState('');
  const contentRef = React.useRef('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [attachments, setAttachments] = useState([]);
  const videoInputRef = React.useRef(null);
  const [showTranslating, setShowTranslating] = useState(false);
  const handleEditorChange = (newContent) => {
    contentRef.current = newContent;
  };

  const handleAttachment = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const url = await uploadFile(file);
      setAttachments(prev => [...prev, { name: file.name, url }]);
    }
  };
  const handlePickVideo = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append('upload', file);
      const res = await axios.post(`/api/ck4upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const { uploaded, url } = res.data || {};
      if (uploaded && url) {
        // 通过全局实例插入视频
        const instances = window.CKEDITOR.instances;
        const firstKey = Object.keys(instances)[0];
        const editor = instances[firstKey];
        if (editor) {
          const html = `<p><video controls style="max-width:100%" src="${url}"></video></p>`;
          editor.insertHtml(html);
        }
      }
    } catch (err) {
      // ignore
    } finally {
      if (videoInputRef.current) videoInputRef.current.value = '';
    }
  };

  const handleSubmit = async () => {
    if (!title || !contentRef.current) {
      setError(t('noticeEditor.required'));
      return;
    }
    setLoading(true);
    setShowTranslating(true);
    setError('');
    try {
      await axios.post('/api/notices', { title, content: contentRef.current, attachments });
      setTitle('');
      contentRef.current = '';
      setShowTranslating(false);
      if (onSuccess) onSuccess();
    } catch (e) {
      setError(e.response?.data?.error || t('noticeEditor.error'));
      setShowTranslating(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className={[theme.surfaces.glassCard, 'rounded-lg shadow-lg p-6 w-full mx-auto text-white'].join(' ')}
      style={{ maxWidth: '60rem', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}
    >
      {/* 公告标题输入 */}
      <input
        className={[theme.inputs.base, 'px-2 py-1 w-full mb-2 rounded text-sm'].join(' ')}
        placeholder={t('noticeEditor.inputTitle')}
        value={title}
        onChange={e => setTitle(e.target.value)}
      />
      {/* 编辑器区域 */}
      <div style={{ height: '39rem', border: '1px solid rgba(255,255,255,0.10)', borderRadius: '8px', overflow: 'hidden', background: 'rgba(255,255,255,0.03)' }}>
        <CKE4Editor onChange={handleEditorChange} placeholder="请输入公告内容..." uploadBase="" />
      </div>
      {error && (
        <div className={[theme.alerts.base, theme.alerts.variant.danger, 'mb-2'].join(' ')}>
          {error}
        </div>
      )}
      {/* 底部按钮行 */}
      <div className="flex justify-end items-center space-x-2 mt-4">
        <button
          type="button"
          onClick={() => {
            try {
              if (typeof window !== 'undefined' && typeof window.openFileManagerModal === 'function') {
                window.openFileManagerModal();
              } else {
                // 动态加载公共脚本后再打开
                const s = document.createElement('script');
                s.src = '/file-manager.js';
                s.defer = true;
                s.onload = () => { try { window.openFileManagerModal && window.openFileManagerModal(); } catch(_) { alert(t('hints.fileManagerLoadFailed')); } };
                s.onerror = () => alert(t('hints.fileManagerScriptLoadFailed'));
                document.head.appendChild(s);
              }
            } catch (e) {
              alert(t('hints.fileManagerLoadFailed'));
            }
          }}
          className={buttonClass({ variant: 'outline', size: 'xs' })}
        >
          文件管理
        </button>
        {/* 上传视频 */}
        <label className={['cursor-pointer', buttonClass({ variant: 'outline', size: 'xs' })].join(' ')}>
          上传视频
          <input ref={videoInputRef} type="file" accept="video/*" style={{ display: 'none' }} onChange={handlePickVideo} />
        </label>
        {/* 附件上传 */}
        <label className={['cursor-pointer', buttonClass({ variant: 'outline', size: 'xs' })].join(' ')}>
          上传附件
          <input type="file" style={{ display: 'none' }} onChange={handleAttachment} />
        </label>
        <button onClick={onCancel} className={buttonClass({ variant: 'ghost', size: 'xs' })}>{t('noticeEditor.cancel')}</button>
        <button onClick={handleSubmit} className={buttonClass({ variant: 'outline', size: 'xs' })} disabled={loading}>
          {loading ? t('noticeEditor.saving') : t('noticeEditor.save')}
        </button>
      </div>
      {/* 附件列表 */}
      {attachments.length > 0 && (
        <ul className="mt-2 list-disc list-inside text-sm text-white/75">
          {attachments.map((att, idx) => (
            <li key={idx}>
              <a 
                href={att.url} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-blue-300 hover:text-blue-200 underline underline-offset-2 transition-colors"
              >
                {att.name}
              </a>
            </li>
          ))}
        </ul>
      )}
      {showTranslating && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 backdrop-blur-sm">
          <div className={[theme.surfaces.glassCard, 'rounded-lg shadow-lg px-8 py-6 text-lg font-semibold text-white'].join(' ')}>
            {t('noticeEditor.translating') || '正在进行多语言翻译，请耐心等待...'}
          </div>
        </div>
      )}
    </div>
  );
};

export default NoticeEditor; 